/**
 * HRSettingsPopover — five knobs + Indeed webhook defaults (default owner
 * dropdown pulled from /hr/settings/eligible-owners, AI screener on/off).
 */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Settings, FileText } from 'lucide-react';
import HRTemplatesManager from './HRTemplatesManager';

const C = { ink: '#2D2833', soft: '#8A7D95', plum: '#685B72' };

export default function HRSettingsPopover({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eligibleOwners, setEligibleOwners] = useState([]);
  const [form, setForm] = useState({
    target_od: 1,
    target_therapists: 4,
    stale_days: 7,
    offer_days: 5,
    max_subordinates: 4,
    jessica_email: '',
    ray_email: '',
    davinder_email: '',
    default_owner_email: '',
    ai_screener_enabled: true,
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get('/hr/settings'),
      api.get('/hr/settings/eligible-owners').catch(() => ({ data: { owners: [] } })),
    ])
      .then(([{ data }, ownersRes]) => {
        const emails = data.assignee_emails || {};
        setEligibleOwners(ownersRes.data?.owners || []);
        setForm({
          target_od: data.default_clinic_target?.od ?? 1,
          target_therapists: data.default_clinic_target?.therapists ?? 4,
          stale_days: data.stale_candidate_days ?? 7,
          offer_days: data.pending_offer_days ?? 5,
          max_subordinates: data.max_subordinates_per_slp ?? 4,
          jessica_email: emails.Jessica || '',
          ray_email: emails.Ray || '',
          davinder_email: emails.Davinder || '',
          default_owner_email: data.default_owner_email || 'ray@thrivespeech.net',
          ai_screener_enabled: data.ai_screener_enabled !== false,
        });
      })
      .catch(() => toast.error('Could not load HR settings'))
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/hr/settings', {
        default_clinic_target: {
          od: Number(form.target_od) || 0,
          therapists: Number(form.target_therapists) || 0,
        },
        stale_candidate_days: Number(form.stale_days) || 0,
        pending_offer_days: Number(form.offer_days) || 0,
        max_subordinates_per_slp: Number(form.max_subordinates) || 0,
        assignee_emails: {
          Jessica:  (form.jessica_email  || '').trim(),
          Ray:      (form.ray_email      || '').trim(),
          Davinder: (form.davinder_email || '').trim(),
        },
        default_owner_email: (form.default_owner_email || '').trim(),
        ai_screener_enabled: !!form.ai_screener_enabled,
      });
      toast.success('HR settings saved');
      setOpen(false);
      onSaved && onSaved();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="hr-settings-trigger" className="gap-2">
          <Settings size={14} /> Settings
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-4 max-h-[80vh] overflow-y-auto" data-testid="hr-settings-popover">
        <div className="text-sm font-bold mb-3" style={{ color: C.ink }}>HR Command Center · Settings</div>
        {loading ? (<div className="text-xs" style={{ color: C.soft }}>Loading…</div>) : (
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider" style={{ color: C.plum }}>Default target seats / clinic</Label>
              <div className="flex gap-2 mt-1">
                <div className="flex-1">
                  <div className="text-[10px]" style={{ color: C.soft }}>Office Directors</div>
                  <Input type="number" min="0" value={form.target_od}
                    onChange={e => setForm({ ...form, target_od: e.target.value })}
                    data-testid="hr-set-target-od" className="h-8" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px]" style={{ color: C.soft }}>FT Therapists (SLP/CF/SLPA)</div>
                  <Input type="number" min="0" value={form.target_therapists}
                    onChange={e => setForm({ ...form, target_therapists: e.target.value })}
                    data-testid="hr-set-target-therapists" className="h-8" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]" style={{ color: C.soft }}>Stale candidate (days)</Label>
                <Input type="number" min="1" value={form.stale_days}
                  onChange={e => setForm({ ...form, stale_days: e.target.value })}
                  data-testid="hr-set-stale-days" className="h-8" />
              </div>
              <div>
                <Label className="text-[10px]" style={{ color: C.soft }}>Pending offer (days)</Label>
                <Input type="number" min="1" value={form.offer_days}
                  onChange={e => setForm({ ...form, offer_days: e.target.value })}
                  data-testid="hr-set-offer-days" className="h-8" />
              </div>
            </div>
            <div>
              <Label className="text-[10px]" style={{ color: C.soft }}>Max subordinates per SLP (CF + SLPA combined)</Label>
              <Input type="number" min="0" value={form.max_subordinates}
                onChange={e => setForm({ ...form, max_subordinates: e.target.value })}
                data-testid="hr-set-max-subordinates" className="h-8" />
            </div>
            <div className="text-[10px] p-2 rounded" style={{ background: '#FBF8F4', color: C.soft }}>
              Clinic on-hold is derived automatically from Speech Evals: if a clinic's Speech evals are Closed in the Scheduling Command Center and the clinic isn't fully staffed, HR flags it as on hold due to staffing. Feeding-evals status is intentionally ignored — only Speech drives the flag.
            </div>

            <div className="border-t pt-3" style={{ borderColor: C.plum + '22' }}>
              <Label className="text-[11px] uppercase tracking-wider" style={{ color: C.plum }}>
                Inbound Indeed applications
              </Label>
              <div className="text-[10px] mb-2" style={{ color: C.soft }}>
                Candidates submitted via Indeed Apply auto-create in the pipeline. Pick who owns them and whether the AI pre-screener runs on each resume.
              </div>
              <div>
                <div className="text-[10px]" style={{ color: C.soft }}>Default owner (Super User + HR/Payroll)</div>
                <Select value={form.default_owner_email || ''}
                        onValueChange={v => setForm({ ...form, default_owner_email: v })}>
                  <SelectTrigger className="h-8" data-testid="hr-set-default-owner">
                    <SelectValue placeholder="Pick default owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleOwners.length === 0 && (
                      <SelectItem value="ray@thrivespeech.net">Ray Zaidan · ray@thrivespeech.net</SelectItem>
                    )}
                    {eligibleOwners.map(o => (
                      <SelectItem key={o.email} value={o.email}>
                        {o.name || o.email} · {o.role === 'hr_payroll' ? 'HR/Payroll' : 'Super User'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <label className="mt-2 flex items-center gap-2 text-[11px] cursor-pointer"
                     style={{ color: C.ink }} data-testid="hr-set-ai-screener-label">
                <input type="checkbox"
                       checked={!!form.ai_screener_enabled}
                       onChange={e => setForm({ ...form, ai_screener_enabled: e.target.checked })}
                       data-testid="hr-set-ai-screener" />
                AI pre-screener runs on every inbound resume
              </label>
              <div className="text-[10px] mt-1" style={{ color: C.soft }}>
                Uses Claude Sonnet 4.6 to score resumes 0–100 against pediatric SLP criteria and surface red flags before HR opens the candidate.
              </div>
            </div>

            <div className="border-t pt-3" style={{ borderColor: C.plum + '22' }}>
              <Label className="text-[11px] uppercase tracking-wider" style={{ color: C.plum }}>
                Follow-up reminder emails
              </Label>
              <div className="text-[10px] mb-2" style={{ color: C.soft }}>
                Overdue action items email the owner every morning at 7:10 CT (idempotent per date). Leave blank to fall back to a users-collection match by first name.
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <div className="text-[10px]" style={{ color: C.soft }}>Jessica</div>
                  <Input type="email" value={form.jessica_email}
                    onChange={e => setForm({ ...form, jessica_email: e.target.value })}
                    placeholder="jessica@thrivespeech.net"
                    data-testid="hr-set-email-jessica" className="h-8" />
                </div>
                <div>
                  <div className="text-[10px]" style={{ color: C.soft }}>Ray</div>
                  <Input type="email" value={form.ray_email}
                    onChange={e => setForm({ ...form, ray_email: e.target.value })}
                    placeholder="ray@thrivespeech.net"
                    data-testid="hr-set-email-ray" className="h-8" />
                </div>
                <div>
                  <div className="text-[10px]" style={{ color: C.soft }}>Davinder</div>
                  <Input type="email" value={form.davinder_email}
                    onChange={e => setForm({ ...form, davinder_email: e.target.value })}
                    placeholder="d.sekhon@thrivespeech.net"
                    data-testid="hr-set-email-davinder" className="h-8" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setTemplatesOpen(true); }}
                      data-testid="hr-settings-open-templates" className="gap-1 mr-auto">
                <FileText size={12} /> Templates
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="hr-settings-save">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
      <HRTemplatesManager open={templatesOpen} onOpenChange={setTemplatesOpen} />
    </Popover>
  );
}
